import Layout from 'components/layout';
import AboutUs from 'components/about-us-page';
import HeadData, { HeadDataType } from 'components/head-data';
import { createNamedContentsApi } from 'data/named-content';
import ContentPage from 'components/content-pages';
import { handleContentPageServerSideProps, isContentPageProps } from 'utils/content-pages';
import {
  isTenantExpCommercial,
  isTenantExpCommercialCa,
  isZoocasaTenant,
  TenantId,
  TenantIds,
  TenantTypes,
} from '@tenants';
import { NOT_FOUND } from 'constants/not-found';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { FeaturesType } from 'contexts/features';

import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import type { AboutUsContent } from 'data/named-content/types';
import { ContentPageProps } from 'types/content-pages';


const handler: HandlerType<AboutUsContent | ContentPageProps> = async (_context: GetServerSidePropsContext, features?: FeaturesType, tenant?: TenantTypes) => {
  if (!isZoocasaTenant(tenant.id, features) && !isTenantExpCommercial(tenant.id, features) && !isTenantExpCommercialCa(tenant.id, features)) {
    return NOT_FOUND;
  }

  const getHeadData = (tenantId: TenantId): HeadDataType => {
    switch (tenantId) {
      case TenantIds.ZOOCASA:
        return {
          title: 'About Us | Zoocasa - Smarter Real Estate Tools & Expert Guidance. ',
          metaDescription: 'Learn about Zoocasa, Canada\'s smarter real estate platform. We combine powerful technology with expert real estate agents to help you buy or sell your home with confidence.',
        };
      case TenantIds.EXP_COMMERCIAL:
        return {
          title: 'About Us - eXp Commercial',
          metaDescription: 'Learn about eXp Commercial, a leader in commercial real estate services. Discover our mission, values, and the team driving innovation in the industry.',
        };
      case TenantIds.EXP_COMMERCIAL_CA:
        return {
          title: 'About Us - eXp Commercial Canada',
          metaDescription: 'Learn about eXp Commercial Canada, a leader in commercial real estate services. Discover our mission, values, and the team driving innovation in the industry.',
        };
      default:
        return {};
    }
  };
  const headData = getHeadData(tenant.id);

  if (isTenantExpCommercialCa(tenant.id, features) || isTenantExpCommercial(tenant.id, features)) {
    return await handleContentPageServerSideProps(tenant.id, [
      TenantIds.EXP_COMMERCIAL,
      TenantIds.EXP_COMMERCIAL_CA,
    ], '/about-us', 'ALL', headData);
  }

  return await getAboutUsContent();
};

export const getServerSideProps = createGetServerSideProps(handler);

export default function AboutUsPage(props: AboutUsContent | ContentPageProps) {
  if (isContentPageProps(props)) {
    const { headData } = props;
    return (
      <Layout showMobileBottomNav={false}>
        {headData && <HeadData data={headData} />}
        <ContentPage siteContent={props.siteContent} />
      </Layout>
    );
  } else {
    const headerData = {
      title: 'About Us | Zoocasa - Smarter Real Estate Tools & Expert Guidance. ',
      metaDescription: 'Learn about Zoocasa, Canada\'s smarter real estate platform. We combine powerful technology with expert real estate agents to help you buy or sell your home with confidence.',
    };
    return (
      <Layout>
        <HeadData data={headerData} />
        <AboutUs story={props.story} mediaCoverage={props.mediaCoverage} surveys={props.surveys} />
      </Layout>
    );
  }
}

async function getAboutUsContent(): Promise<GetServerSidePropsResult<AboutUsContent>> {
  const namedContentsApi = createNamedContentsApi();
  const config = await namedContentsApi.getAboutUsNamedContent();
  const content = (config && config?.content) || { story: [], mediaCoverage: [], surveys: []};
  return {
    props: content,
  };
}